import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  HiOutlineVideoCamera,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlineExternalLink,
  HiOutlineDuplicate,
  HiOutlineCheck,
  HiOutlineClock,
  HiOutlineUserGroup,
  HiOutlineOfficeBuilding,
} from 'react-icons/hi';

/**
 * Google Meet Integration (Calendar section, admin + superadmin panels).
 * Schedule and manage online meetings directly from the application:
 *  - pick a date/time and open meet.google.com/new in one click to get the
 *    Google Meet link,
 *  - paste the link into the meeting, save it in the CRM,
 *  - every scheduled meeting is also published on the calendar feed, so it
 *    lands on the connected Google Calendar with the join link.
 *
 * Props:
 *   scope          — unique query key suffix ('org' | 'superadmin')
 *   listMeetings   — (params) => promise → { data: [meeting] }
 *   createMeeting  — (body) => promise
 *   updateMeeting  — (id, body) => promise
 *   showOrganization — show the organization name column (superadmin)
 */

const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (d) => {
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (t) => {
  if (!t) return '';
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
};

const DURATIONS = [15, 30, 45, 60, 90];

const GoogleMeetSection = ({
  scope,
  listMeetings,
  createMeeting,
  updateMeeting,
  showOrganization = false,
}) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // form state
  const emptyForm = {
    title: '',
    scheduledDate: '',
    scheduledTime: '',
    durationMinutes: 30,
    meetLink: '',
    attendees: '',
    description: '',
  };
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading } = useQuery({
    queryKey: ['meetings', scope],
    queryFn: async () => {
      const response = await listMeetings({});
      return response?.data?.data || [];
    },
    refetchInterval: 120000,
  });

  const meetings = Array.isArray(data) ? data : [];

  const createMutation = useMutation({
    mutationFn: createMeeting,
    onSuccess: () => {
      queryClient.invalidateQueries(['meetings', scope]);
      toast.success('Meeting scheduled — it will appear on the Google Calendar feed');
      setForm(emptyForm);
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to schedule meeting');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }) => updateMeeting(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries(['meetings', scope]);
      toast.success('Meeting updated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to update meeting');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.scheduledDate) {
      toast.error('Meeting title and date are required');
      return;
    }
    createMutation.mutate({
      title: form.title.trim(),
      description: form.description,
      scheduledDate: form.scheduledDate,
      scheduledTime: form.scheduledTime,
      durationMinutes: form.durationMinutes,
      meetLink: form.meetLink,
      attendees: form.attendees,
    });
  };

  const handleCopyLink = async (m) => {
    try {
      await navigator.clipboard.writeText(m.meetLink || '');
      setCopiedId(m._id);
      toast.success('Meet link copied');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const openMeetNew = () => {
    // Google's instant-meeting shortcut: creates a new Meet link when opened
    window.open('https://meet.google.com/new', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineVideoCamera className="w-5 h-5 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-gray-900">Google Meet Integration</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Allows users to schedule and manage online meetings directly from the application.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors whitespace-nowrap"
        >
          {showForm ? (
            <HiOutlineX className="w-4 h-4" />
          ) : (
            <HiOutlinePlus className="w-4 h-4" />
          )}
          {showForm ? 'Close' : 'New Meeting'}
        </button>
      </div>

      {/* New meeting form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Meeting title *
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Weekly client demo"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={form.scheduledDate}
                onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={form.scheduledTime}
                onChange={(e) => setForm({ ...form, scheduledTime: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={form.durationMinutes}
                onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} minutes
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Google Meet link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.meetLink}
                  onChange={(e) => setForm({ ...form, meetLink: e.target.value })}
                  placeholder="https://meet.google.com/…"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={openMeetNew}
                  title="Opens meet.google.com/new — the Meet link is generated there; copy it and paste here"
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-green-700 hover:bg-green-50 transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  <HiOutlineExternalLink className="w-3.5 h-3.5" />
                  Get link
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Attendees (comma-separated emails)
              </label>
              <input
                type="text"
                value={form.attendees}
                onChange={(e) => setForm({ ...form, attendees: e.target.value })}
                placeholder="client@example.com, manager@yourcompany.com"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Agenda / notes</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What will be discussed in this meeting?"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            💡 Click <strong>Get link</strong> to open Google Meet and copy the generated link
            into the field — the meeting (with its join link) is also published on the calendar
            feed so it shows on the connected Google Calendar.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm);
                setShowForm(false);
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Scheduling…' : 'Schedule Meeting'}
            </button>
          </div>
        </form>
      )}

      {/* Meetings list */}
      {isLoading ? (
        <div className="py-8 flex justify-center">
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="py-8 text-center">
          <HiOutlineVideoCamera className="w-8 h-8 text-gray-300 mx-auto" />
          <p className="mt-2 text-sm text-gray-500">
            No meetings scheduled yet — click <strong>New Meeting</strong> to schedule one.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {meetings.map((m) => {
            const past = m.status === 'completed';
            return (
              <li
                key={m._id}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                  past
                    ? 'border-gray-100 bg-gray-50 opacity-70'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Date block */}
                <div className="w-16 flex-shrink-0 text-center rounded-lg bg-gray-50 border border-gray-100 py-1.5">
                  <p className="text-[11px] font-semibold uppercase text-red-500">
                    {new Date(m.scheduledDate).toLocaleDateString(undefined, { month: 'short' })}
                  </p>
                  <p className="text-lg font-bold text-gray-900 leading-none">
                    {pad2(new Date(m.scheduledDate).getDate())}
                  </p>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-semibold truncate ${
                      past ? 'text-gray-500 line-through' : 'text-gray-900'
                    }`}
                  >
                    {m.title}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap mt-0.5 text-xs text-gray-500">
                    {m.scheduledTime && (
                      <span className="flex items-center gap-1">
                        <HiOutlineClock className="w-3.5 h-3.5" />
                        {formatTime(m.scheduledTime)} · {m.durationMinutes} min
                      </span>
                    )}
                    {m.attendees?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <HiOutlineUserGroup className="w-3.5 h-3.5" />
                        {m.attendees.length} attendee{m.attendees.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {showOrganization && m.organizationId?.name && (
                      <span className="flex items-center gap-1">
                        <HiOutlineOfficeBuilding className="w-3.5 h-3.5" />
                        {m.organizationId.name}
                      </span>
                    )}
                  </div>
                  {m.description && (
                    <p className="mt-1 text-xs text-gray-500 line-clamp-2">{m.description}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  {m.meetLink ? (
                    <>
                      <a
                        href={m.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors"
                      >
                        <HiOutlineVideoCamera className="w-3.5 h-3.5" />
                        Join
                      </a>
                      <button
                        onClick={() => handleCopyLink(m)}
                        title="Copy Meet link"
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                      >
                        {copiedId === m._id ? (
                          <HiOutlineCheck className="w-3.5 h-3.5 text-green-600" />
                        ) : (
                          <HiOutlineDuplicate className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">No Meet link yet</span>
                  )}
                  {!past && (
                    <>
                      <button
                        onClick={() => updateMutation.mutate({ id: m._id, status: 'completed' })}
                        title="Mark as completed"
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Done
                      </button>
                      <button
                        onClick={() => updateMutation.mutate({ id: m._id, status: 'cancelled' })}
                        title="Cancel meeting"
                        className="px-2.5 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default GoogleMeetSection;